import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Box, ButtonBase, Chip, CircularProgress, Typography } from '@mui/material';
import { DocumentText, Import } from 'iconsax-react';
import { palette } from '../../../../theme/palette';
import { ClientContractStatus } from '@application/common/enums/client-contract-status';
import { ContractAutoGeneratedType } from '@application/common/enums/contract-auto-generated-type';
import { formatDisplayDate } from '@infrastructure/helpers';
const theme = palette('light');
// ── contract presentation helpers (only use fields already present on UserContractDto) ──
const contractKind = (type) => {
    switch (type) {
        case ContractAutoGeneratedType.SECONDMENTAGREEMENTFIXEDTIMEPHASEB:
        case ContractAutoGeneratedType.SECONDMENTAGREEMENTFIXEDTIMEPHASEBEN:
        case ContractAutoGeneratedType.DETACHERINGSOVEREENKOMSTBEPAALDETIJDFASEB:
        case ContractAutoGeneratedType.DETACHERINGSOVEREENKOMSTONBEPAALDETIJDFASEC:
        case ContractAutoGeneratedType.SECONDMENTAGREEMENTINDEFINITEPERIODPHASECEN:
        case ContractAutoGeneratedType.DETACHERINGSOVEREENKOMSTONBEPAALDETIJDFASECNL:
            return 'Secondment agreement';
        case ContractAutoGeneratedType.UITZENDOVEREENKOMSTBEPAALDETIJDFASEA:
        case ContractAutoGeneratedType.AGENCYWORKEMPLOYMENTFIXEDTIMEPHASEAEN:
        case ContractAutoGeneratedType.AGENCYWORKEMPLOYMENTFIXEDTIMEPHASEANL:
            return 'Agency work agreement';
        default:
            return 'Contract';
    }
};
export const contractPeriod = (contract) => {
    const start = formatDisplayDate(contract.contactStartDate);
    const end = formatDisplayDate(contract.contactEndDate);
    if (start && end)
        return `${start} – ${end}`;
    if (start)
        return `from ${start}`;
    if (end)
        return `until ${end}`;
    return '';
};
export const contractTitle = (contract) => {
    const period = contractPeriod(contract);
    return period ? `${contractKind(contract.contractAutoGeneratedType)} · ${period}` : contractKind(contract.contractAutoGeneratedType);
};
/** All non-deleted files of a contract (generated contract + attached documents), de-duplicated. */
export const contractFiles = (contract) => {
    const all = [...(contract.clientContracts ?? []), ...(contract.clientContractDocuments ?? [])].filter((f) => f && !f.isDeleted);
    const unique = new Map();
    all.forEach((f) => {
        const key = f.id || `${f.folderName}/${f.name}/${f.autoGeneratedContractLink ?? ''}`;
        if (!unique.has(key))
            unique.set(key, f);
    });
    return Array.from(unique.values());
};
const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
/** Human label for an attachment; generated names containing a UUID are replaced. */
export const isGeneratedName = (name) => !name || UUID_PATTERN.test(name);
export const fileLabel = (file, index) => (isGeneratedName(file.name) ? `Document ${index + 1}` : file.name);
export const sortByStartDesc = (contracts) => [...contracts].sort((a, b) => (b.contactStartDate || '').localeCompare(a.contactStartDate || '') || a.id.localeCompare(b.id));
export const contractBadge = (contract) => {
    switch (contract.status) {
        case ClientContractStatus.PendingSignature:
            return { label: 'To sign', color: '#960041', background: '#FFE3EF' };
        case ClientContractStatus.Signed:
            return { label: 'Awaiting approval', color: '#C27800', background: '#FFF4E1' };
        case ClientContractStatus.Approved: {
            const started = !contract.contactStartDate || new Date(contract.contactStartDate).getTime() <= Date.now();
            return started
                ? { label: 'Active', color: '#316F1D', background: '#E5FEDE' }
                : { label: 'Approved', color: '#1D5C84', background: '#D9F3FF' };
        }
        case ClientContractStatus.Rejected:
            return { label: 'Declined', color: '#492A2A', background: '#FDBCBC' };
        case ClientContractStatus.Expired:
            return { label: 'Expired', color: '#616161', background: '#E0E0E0' };
        default:
            return undefined;
    }
};
/**
 * Same look as the shared DownloadAction row, but the whole row is a single button
 * (keyboard accessible) and the redundant "Download" text is omitted.
 */
export const DocumentRow = ({ title, secondary, badge, muted = false, disabled = false, loading = false, actionLabel, onClick }) => {
    const isDisabled = disabled || loading;
    return (_jsxs(ButtonBase, { onClick: () => {
            if (!isDisabled)
                onClick();
        }, disabled: isDisabled, "aria-label": actionLabel || `Open ${title}`, "aria-busy": loading || undefined, focusRipple: true, sx: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            width: '100%',
            textAlign: 'left',
            color: isDisabled ? '#A0A0A0' : muted ? '#616161' : 'black',
            background: isDisabled ? '#E0E0E0' : muted ? '#F0F0F0' : theme.primary.light,
            borderRadius: '13px',
            padding: '10px 15px',
            minHeight: 56,
            opacity: disabled ? 0.5 : 1,
            '&:hover': { backgroundColor: isDisabled ? '#E0E0E0' : muted ? '#E6E6E6' : theme.primary.lighter },
            '&.Mui-focusVisible': { outline: `2px solid ${theme.primary.dark}`, outlineOffset: 2 }
        }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flexGrow: 1 }, children: [_jsx(DocumentText, { "aria-hidden": 'true', style: { flexShrink: 0 } }), _jsxs(Box, { sx: { minWidth: 0, flexGrow: 1 }, children: [_jsx(Typography, { variant: 'caption', component: 'div', color: 'inherit', fontWeight: 500, sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, title: title, children: title }), (secondary || badge) && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, minWidth: 0 }, children: [badge && (_jsx(Chip, { label: badge.label, size: 'small', sx: { height: 20, fontSize: '11px', fontWeight: 500, borderRadius: '20px', color: badge.color, backgroundColor: badge.background } })), secondary && (_jsx(Typography, { variant: 'caption', color: 'inherit', sx: { opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, title: secondary, children: secondary }))] }))] })] }), _jsx(Box, { sx: { display: 'flex', alignItems: 'center', flexShrink: 0 }, "aria-hidden": 'true', children: loading ? _jsx(CircularProgress, { size: 20, color: 'inherit' }) : _jsx(Import, {}) })] }));
};
